import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { type LinkComponent, SearchFooter } from '@atlassian/search-dialog';
import { JiraIcon } from '@atlaskit/temp-nav-app-icons/jira';
import { ConfluenceIcon } from '@atlaskit/temp-nav-app-icons/confluence';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { useUserContext } from '../../common/user-context';
import { useQuery } from '../query-context';
import {
	AdvancedSearchFooter,
	AdvancedSearchFooterLink,
	type AdvancedSearchFooterProps,
	ReturnIconWithModifier,
} from './advanced-search-footer';
import { messages } from '../messages';
import { getDefaultAtlasBaseURL } from '../products/townsquare/url-generators';

import { GoogleDriveIcon } from './icons/google-icon';
import { SlackIcon } from './icons/slack-icon';
import { useSessionUserInput } from '../user-input-provider';
import {
	getTrigger,
	isNewTab,
	onAdvancedSearchSelected,
	useAnalytics,
} from '../../common/analytics';
import { useProductContext } from '../product-router';

const ModifierIcon = () => <ReturnIconWithModifier showModifierKey />;
const ReturnIcon = () => <ReturnIconWithModifier />;

const containerStyles = xcss({
	display: 'flex',
});

const productIconStyles = xcss({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	width: '78px',
});

const moreButtonStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: 'border.radius.100',
	paddingInline: 'space.075',
	marginRight: 'space.050',
	height: '20px',
	display: 'flex',
	alignItems: 'center',
});

const absoluteStyles = xcss({
	position: 'absolute',
});

const borderStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '22px',
	width: '22px',
	borderRadius: '6px',
});

const jiraIconOverlapStyles = xcss({
	zIndex: 'dialog',
	transform: 'translate(15px, 0)',
});

const confluenceIconOverlapStyles = xcss({
	zIndex: 'navigation',
	transform: 'translate(30px, 0)',
});

const googleDriveIconOverlapStyles = xcss({
	zIndex: 'card',
	transform: 'translate(45px, 0)',
});

const slackIconOverlapStyles = xcss({
	transform: 'translate(60px, 0)',
});

const IconComponent = ({ showModifierIcon }: { showModifierIcon?: boolean }) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={containerStyles}>
			<Box xcss={productIconStyles}>
				<Box xcss={[absoluteStyles, jiraIconOverlapStyles, borderStyles]}>
					<JiraIcon size="20" />
				</Box>
				<Box xcss={[absoluteStyles, confluenceIconOverlapStyles, borderStyles]}>
					<ConfluenceIcon size="20" />
				</Box>
				<Box xcss={[absoluteStyles, googleDriveIconOverlapStyles]}>
					<GoogleDriveIcon />
				</Box>
				<Box xcss={[absoluteStyles, slackIconOverlapStyles]}>
					<SlackIcon />
				</Box>
			</Box>
			<Box xcss={moreButtonStyles}>
				<Text size="small" align="center" color="color.text.subtle">
					{formatMessage(messages.more)}
				</Text>
			</Box>
			{showModifierIcon ? <ModifierIcon /> : <ReturnIcon />}
		</Box>
	);
};

export const getAtlassianHomeSearchUrl = (
	orgId: string,
	query?: string,
	primaryProduct?: string,
) => {
	const url = new URL(`${getDefaultAtlasBaseURL(true)}/o/${orgId}/search`);

	if (primaryProduct) {
		url.searchParams.set('referrer', primaryProduct);
	}

	if (query) {
		url.searchParams.set('text', query);
	}

	return url.toString();
};

export const AtlassianHomeSearchLink = ({
	orgId,
	linkComponent,
	showModifierIcon,
}: {
	orgId: string;
	linkComponent?: LinkComponent;
	showModifierIcon?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const { query, isLoading } = useQuery();
	const { resetSearchSession } = useSessionUserInput();
	const { fireAnalyticsEvent } = useAnalytics();
	const { getActiveProduct, getPrimaryProduct } = useProductContext();
	const primaryProduct = getPrimaryProduct();
	const activeProduct = getActiveProduct();
	const href = getAtlassianHomeSearchUrl(orgId, query, primaryProduct?.id);

	const productName = activeProduct?.title;
	const actionSubjectId = `${productName}AdvancedSearchLink`;

	const onAdvancedSearchClicked = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			resetSearchSession();
			fireAnalyticsEvent(
				onAdvancedSearchSelected({
					trigger: getTrigger(e),
					actionSubjectId: actionSubjectId,
					isLoading,
					newTab: isNewTab(e),
					isRovoCrossProductSearchLink: true,
				}),
			);
		},
		[fireAnalyticsEvent, isLoading, actionSubjectId, resetSearchSession],
	);

	return (
		<AdvancedSearchFooterLink
			href={href}
			onClick={onAdvancedSearchClicked}
			linkComponent={linkComponent}
			contentLabel={formatMessage(messages.extensible_advanced_search_cross_product_footer)}
			returnKeyIconComponent={() => <IconComponent showModifierIcon={showModifierIcon} />}
		/>
	);
};

export const AtlassianHomeSearchFooter = (props: AdvancedSearchFooterProps) => {
	const { orgId } = useUserContext();
	return (
		<>
			<SearchFooter>
				<AdvancedSearchFooter {...props} returnKeyIconComponent={ReturnIcon} />
			</SearchFooter>
			{orgId && (
				<SearchFooter>
					<AtlassianHomeSearchLink
						orgId={orgId}
						linkComponent={props.linkComponent}
						showModifierIcon
					/>
				</SearchFooter>
			)}
		</>
	);
};
